/*
 * (C) 2026 BeyondDemise, All Rights Reserved.
 *
 * English UI message catalog (#89) — the source of truth for message
 * keys. Every other locale (`de.ts`, …) must provide exactly this key
 * set; TypeScript enforces the parity via the `UiMessages` type below.
 *
 * Conventions:
 *   - Keys are `component.section.item` in camelCase.
 *   - `{param}` placeholders are substituted by `t()` (see
 *     `src/services/i18n.ts`); parameter names are part of the key's
 *     contract and must match across locales.
 *   - Strings are byte-identical to the pre-i18n hardcoded UI text so
 *     the English UI (and its tests) render unchanged.
 */

export const en = {
  // Shared
  'common.cancel': 'Cancel',
  'common.back': 'Back',
  'common.continue': 'Continue',
  'common.done': 'Done',
  'common.demo': 'Demo',

  // Login/challenge shell chrome
  'login.backToWebsite': 'Back to website',
  'login.tagline': 'Keeping secrets safe',

  // TdChallenge
  'challenge.sendCodeInfo': 'We will send you your 6 digit access code.',
  'challenge.emailLabel': 'Email',
  'challenge.emailPlaceholder': 'Enter your email',
  'challenge.sendCode': 'Send Code',

  // TdLogin
  'login.emailLabel': 'Email',
  'login.codeLabel': 'Code',
  'login.codePlaceholder': 'Your 6 digit code ',
  'login.signIn': 'Sign In',

  // TdSetup
  'setup.heading': 'Getting the application ready',
  'setup.state': 'State: {state}',
  'setup.progressLabel': 'Application setup progress',

  // TdOnboarding
  'onboarding.welcome': 'Welcome to Beyond Demise',
  'onboarding.intro1':
    "Let's get you started with your journey.We will collect some basic information about you to get you started.",
  'onboarding.intro2':
    'And setup your passphrase to secure your disclossures. You can change this later.',
  'onboarding.emailLabel': 'Email',
  'onboarding.fullNameLabel': 'FullName',
  'onboarding.fullNamePlaceholder': 'John Doe',
  'onboarding.salutationLabel': 'Salutation',
  'onboarding.salutationPlaceholder': 'DearJohn,',
  'onboarding.birthdayLabel': 'Birthday',
  'onboarding.termsAccept': 'I have read and accept the',
  'onboarding.termsLink': 'Terms and conditions',
  'onboarding.submit': 'Save and continue to passphrase',

  // TdDashboard
  'dashboard.banner': 'Invite friends and family to join you.',
  'dashboard.navLabel': 'Main navigation',
  'dashboard.nav.home': 'Home',
  'dashboard.nav.about': 'About',
  'dashboard.nav.contact': 'Contact',
  'dashboard.nav.settings': 'Settings',
  'dashboard.asideLabel': 'Account and actions',
  'dashboard.logout': 'Logout',
  'dashboard.createNew': 'Create New',
  'dashboard.crumb': 'Dashboard',
  'dashboard.acceptance.waiting': 'Waiting for acceptance',
  'dashboard.acceptance.pending': 'Pending your acceptance',
  'dashboard.footer.service': 'Our Service',
  'dashboard.footer.privacy': 'Privacy Policy',
  'dashboard.footer.terms': 'Terms of Service',
  'dashboard.footer.contact': 'Contact Us',
  'dashboard.footer.aboutUs': 'About Us',
  'dashboard.footer.aboutTd': 'About Triggered Disclosure',
  'dashboard.footer.mission': 'Our Mission',
  'dashboard.footer.team': 'Our Team',
  'dashboard.footer.discover': 'Discover',
  'dashboard.footer.products': 'Our Products',
  'dashboard.footer.services': 'Our Services',
  'dashboard.footer.blog': 'Our Blog',
  'dashboard.localeLabel': 'Language',

  // TdNavigation (breadcrumbs)
  'nav.home': 'Home',

  // TdStorylist
  'storylist.title': 'Dashboard',
  'storylist.defaultType': 'Story',
  'storylist.openStory': 'Open story: {type}',

  // TdNodata
  'nodata.empty': 'No Farewells or Disclosures',
  'nodata.getStarted': 'Get Started',

  // TdAccount
  'account.title': 'Your account',
  'account.level': 'Level: {level}',
  'account.nextBilling': 'Next billing: {date}',
  'account.waiting': 'waiting for acceptance: {count}',
  'account.pending': 'pending your acceptance: {count}',
  'account.loading': 'Loading account information...',
  'account.fingerprintTitle': 'Your identity fingerprint',
  'account.copyFingerprint': 'Copy fingerprint',
  'account.fingerprintHint':
    "Share this with recipients out-of-band so they can verify they're sending to the real you.",

  // TdAcceptance
  'acceptance.loading': 'Loading acceptance information...',
  'acceptance.none': 'No pending acceptances',

  // TdReminder
  'reminder.title': 'Next reminder',
  'reminder.loading': 'Loading reminder information...',
  'reminder.noDate': 'no date',
  'reminder.na': 'n/a',

  // TdStoryForm
  'storyform.typeQuestion': 'What story do you want to tell?',
  'storyform.typeHint': 'This cannot be changed after saving',
  'storyform.type.disclosure': 'Disclosure',
  'storyform.type.farewell': 'Farewell',
  'storyform.tab.who': '1. Who',
  'storyform.tab.what': '2. What',
  'storyform.tab.when': '3. When',
  'storyform.who.heading':
    'Select the people you want to be informed when your time is up.',
  'storyform.who.noteLabel': 'NOTE:',
  'storyform.who.note1':
    'They need to sign up for a free account to ensure they will be able to read your',
  'storyform.who.noteEncrypted': 'encrypted',
  'storyform.who.note2': 'message.',
  'storyform.addParticipant': 'Add Participant',
  'storyform.table.who': 'Who',
  'storyform.table.email': 'Email',
  'storyform.table.confirmed': 'Confirmed',
  'storyform.table.fingerprint': 'Fingerprint',
  'storyform.table.actions': 'Actions',
  'storyform.table.yes': 'Yes',
  'storyform.table.no': 'No',
  'storyform.unverified': 'unverified',
  'storyform.delete': 'Delete',
  'storyform.what.intro': 'Share what you have to tell',
  'storyform.what.headline': 'Headline*',
  'storyform.what.headlinePlaceholder': 'Enter a headline (unencrypted)',
  'storyform.what.message': 'Message',
  'storyform.what.messageHint':
    'Share your story — Markdown formatting is supported',
  'storyform.what.messagePlaceholder': 'Write your message…',
  'storyform.what.headlineNote':
    'The headline is used to identify the story and is unencrypted.',
  'storyform.when.question': 'Release this message when ...',
  'storyform.when.timeout': 'I have not checked in',
  'storyform.when.custodian': 'My custodian has confirmed my passing',
  'storyform.when.bothPrefix': 'Both: I have not checked in',
  'storyform.when.and': 'AND',
  'storyform.when.eitherPrefix': 'Either I have not checked in',
  'storyform.when.or': 'OR',
  'storyform.when.custodianConfirmed': 'my custodian has confirmed my passing',
  'storyform.when.pro': 'PRO',
  'storyform.when.checkInLabel': 'Check in latest every',
  'storyform.when.unitLabel': 'Unit',
  'storyform.when.unit.days': 'days',
  'storyform.when.unit.weeks': 'weeks',
  'storyform.when.unit.month': 'month',
  'storyform.when.remind': 'We will remind you',
  'storyform.when.reminderInfo': 'Information',
  'storyform.unlockAlert': 'Please unlock the vault to save the story',
  'storyform.saveFailed': 'Failed to process and save story',

  // TdStoryAction
  'storyaction.stage.who': 'Who',
  'storyaction.stage.what': 'What',
  'storyaction.stage.when': 'When',
  'storyaction.save': 'Save',
  'storyaction.unlockToSave': 'Unlock (🔐) Vault to save',

  // TdAddParticipant
  'addparticipant.title': 'Add Participant',
  'addparticipant.nameLabel': 'Full Name',
  'addparticipant.namePlaceholder': 'Enter full name',
  'addparticipant.emailLabel': 'Email Address',
  'addparticipant.emailPlaceholder': 'Enter email address',
  'addparticipant.save': 'Save Participant',

  // TdPassphrase
  'passphrase.tab': 'Passphrase',
  'passphrase.saveTab': 'Save',
  'passphrase.heading': 'Create a passphrase',
  'passphrase.clearAll': 'Clear all words',
  'passphrase.intro1': 'You need a',
  'passphrase.introStrong': 'six word passphrase',
  'passphrase.intro2': 'to secure your disclosures. Keep it safe.',
  'passphrase.intro3': 'This form helps you to come up with one.',
  'passphrase.intro4': 'Keep it safe!',
  'passphrase.languageLabel': 'Language',
  'passphrase.comingSoon': '{language} (coming soon)',
  'passphrase.wordLabel': 'Word {n}',
  'passphrase.wordPlaceholder': 'Enter word {n}',
  'passphrase.randomWord': 'Pick a random word {n}',
  'passphrase.generateAll': 'Create six random words',
  'passphrase.savePrint': 'Save & Print',
  'passphrase.openDashboard': 'Open your dashboard',
  'passphrase.fillAllAlert': 'Please fill in all 6 words before saving.',

  // TdVaultLock
  'vaultlock.unlockVault': 'Unlock vault',
  'vaultlock.lockVault': 'Lock vault',
  'vaultlock.drawerLabel': 'Passphrase required',
  'vaultlock.wordLabel': 'Word {n}',
  'vaultlock.wordPlaceholder': 'word{n}',
  'vaultlock.unlock': 'Unlock',
  'vaultlock.unlocking': 'Unlocking...',
  'vaultlock.failedRetry': 'Failed - Try Again',
  'vaultlock.forgot': 'Forgot passphrase?',
  'vaultlock.forgotTitle': 'Recover your passphrase from your printed backup',

  // TdForgotPass
  'forgotpass.dialogLabel': 'Forgot passphrase',
  'forgotpass.modeQuestion': 'How do you want to enter your recovery code?',
  'forgotpass.mode.paste': 'Paste the backup text',
  'forgotpass.mode.camera': 'Scan the QR code with the camera',
  'forgotpass.mode.upload': 'Upload an image of the printed page',
  'forgotpass.pasteIntro':
    'Paste the recovery backup text exactly as printed. Whitespace and newlines are ignored.',
  'forgotpass.recover': 'Recover passphrase',
  'forgotpass.cameraPoint': 'Point your camera at the printed QR code.',
  'forgotpass.scanning': 'Scanning for a QR code…',
  'forgotpass.uploadIntro':
    'Upload a photo or scan of the printed recovery page. The QR code will be read from the image.',
  'forgotpass.chooseImage': 'Choose image…',
  'forgotpass.submitting': 'Recovering your passphrase…',
  'forgotpass.successIntro': 'Your vault passphrase has been recovered:',
  'forgotpass.successHint1': 'Click',
  'forgotpass.successHintStrong': 'Use this passphrase',
  'forgotpass.successHint2':
    'to unlock your vault now, or write the words down before closing this dialog.',
  'forgotpass.usePassphrase': 'Use this passphrase',
  'forgotpass.tryAgain': 'Try again',
  'forgotpass.recoveryFailed': 'Recovery failed.',
  'forgotpass.error.cameraUnsupported':
    'Camera access is not supported in this browser. Go back and choose another option.',
  'forgotpass.error.cameraDenied':
    'Camera access was denied. Allow camera access in your browser settings, or go back and choose another option.',
  'forgotpass.error.cameraNotFound':
    'No usable camera was found on this device. Go back and choose another option.',
  'forgotpass.error.cameraBusy':
    'The camera is already in use by another application. Close it and try again, or go back and choose another option.',
  'forgotpass.error.cameraFailed':
    'The camera could not be started. Go back and choose another option.',
  'forgotpass.error.noQr':
    'No QR code was found in that image. Try a sharper photo of the printed page, or go back and paste the backup text instead.',
  'forgotpass.error.invalidBlob':
    'The pasted text does not look like a valid recovery blob. Please copy the backup text exactly as printed.',
  'forgotpass.error.authFailed':
    'The recovery blob does not belong to this account, or has been tampered with.',
  'forgotpass.error.sessionExpired':
    'Your session has expired. Please log in again.',
  'forgotpass.error.rateLimited':
    'Too many recovery attempts. Please wait and try again later.',
  'forgotpass.error.malformedBundle':
    'The recovery blob is malformed. Please copy the backup text exactly as printed.',
  'forgotpass.error.malformedResponse':
    'The server returned an unexpected response. Please try again later.',
  'forgotpass.error.serverError':
    'The server is currently unavailable. Please try again later.',
  'forgotpass.error.networkError':
    'Could not reach the server. Check your connection and try again.',
  'forgotpass.error.unexpected':
    'An unexpected error occurred. Please try again.',

  // TdPrintQr
  'printqr.summary': 'Your passphrase',
  'printqr.qrTitle': 'QR Code',
  'printqr.backupTitle': 'Backup text when the qr code fails',

  // TdUserProfile
  'userprofile.title': 'Profile & account',
  'userprofile.tab.profile': 'Profile',
  'userprofile.tab.account': 'Account',
  'userprofile.yourProfile': 'Your profile',
  'userprofile.emailLabel': 'Email:',
  'userprofile.notSignedIn': 'not signed in',
  'userprofile.rolesLabel': 'Roles:',
  'userprofile.vaultLabel': 'Vault:',
  'userprofile.vaultUnlocked': 'unlocked',
  'userprofile.vaultLocked': 'locked',
  'userprofile.fingerprintTitle': 'Your identity fingerprint',
  'userprofile.unlockHint':
    'Unlock your vault to see your identity fingerprint.',
  'userprofile.copyFingerprint': 'Copy fingerprint',
  'userprofile.logout': 'Log out',

  // TdMessageAdmin (page chrome only — template content is
  // backend-owned data and stays untranslated)
  'msgadmin.gate':
    'Message administration is available to administrators only.',
  'msgadmin.title': 'Message templates',
  'msgadmin.list': 'Templates',
  'msgadmin.edit': 'Edit',
  'msgadmin.subject': 'Subject:',
  'msgadmin.placeholders': 'Placeholders:',
  'msgadmin.preview': 'Preview',
  'msgadmin.noSelection': 'Select a template to edit and preview it.',

  // TdMarkdownEditor toolbar
  'mde.bold': 'Bold',
  'mde.italic': 'Italic',
  'mde.heading': 'Heading',
  'mde.quote': 'Quote',
  'mde.bulletList': 'Bullet list',
  'mde.numberedList': 'Numbered list',
  'mde.insertLink': 'Insert link',
  'mde.preview': 'Preview',
  'mde.renderingPreview': 'Rendering preview…',
} as const;

/** Union of every message key — locales must cover this exactly. */
export type MessageKey = keyof typeof en;

/** Shape every locale catalog must satisfy (full key parity with `en`). */
export type UiMessages = Readonly<Record<MessageKey, string>>;
